import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Carousel } from "react-bootstrap";
import { MdChevronLeft, MdChevronRight, MdZoomIn } from "react-icons/md";
import styles from "./CompareZoomModal.module.css";

const CompareZoomModal = ({
  modalId = "compareZoomModal",
  items = [],
  initialIndex = 0,
  totalItems = 0,
  onClose,
  onZoom,
}) => {
  // Transform items with models array to flat structure.
  // Memoized to keep reference stable across renders.
  const transformedItems = useMemo(
    () =>
      items.map((item) => {
        const transformed = {
          ...item,
          // Ensure personId exists for component compatibility
          personId: item.personId || item.ModelId,
        };
        if (item.models && Array.isArray(item.models)) {
          item.models.forEach((model, idx) => {
            transformed[`image${idx + 1}`] = model.resultImage;
            transformed[`title${idx + 1}`] = model.title;
            transformed[`time${idx + 1}`] = model.timeSeconds;
            transformed[`key${idx + 1}`] = model.key;
          });
        }
        return transformed;
      }),
    [items]
  );

  const [index, setIndex] = useState(initialIndex);
  const [isFlipped, setIsFlipped] = useState(false);
  const [sourcePreviewKey, setSourcePreviewKey] = useState("person");
  const [resultPreviewKey, setResultPreviewKey] = useState("image1");

  const isNanoBanana2 = (title = "") =>
    title.trim().toLowerCase() === "nano banana 2 (resolution - 1k) ";

  useEffect(() => {
    setIndex(initialIndex);
    setIsFlipped(false);
    setSourcePreviewKey("person");
    setResultPreviewKey("image1");
  }, [initialIndex]);

  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleFlip = () => {
    setIsFlipped((prev) => !prev);
  };

  const handleZoomImage = (image) => {
    if (image && onZoom) onZoom(image);
  };

  const currentItem = transformedItems[index] || {};

  const sourcePreviewOptions = [
    {
      key: "person",
      title: `Person ${currentItem.personId ?? ""}`.trim(),
      subtitle: "Primary subject",
      image: currentItem.ModelImage,
      altText: `Person ${currentItem.personId ?? ""}`.trim(),
    },
    {
      key: "product",
      title: `Product ${currentItem.productId ?? ""}`.trim(),
      subtitle: "Garment reference",
      image: currentItem.productImage,
      altText: `Product ${currentItem.productId ?? ""}`.trim(),
    },
  ].filter((option) => option.image);

  const resultPreviewOptions = [
    {
      key: "image1",
      title: currentItem.title1,
      subtitle: currentItem.time1 ? `${currentItem.time1} sec` : "Nano Banana 2 · 0.5K",
      image: currentItem.image1,
      altText: "Nano Banana 2 (Resolution - 0.5K) Result",
      toneClass: styles.imageTone0,
    },
    {
      key: "image2",
      title: currentItem.title2,
      subtitle: currentItem.time2 ? `${currentItem.time2} sec` : "Nano Banana 2 · 1K",
      image: currentItem.image2,
      altText: "Nano Banana 2 (Resolution - 1K) Result",
      toneClass: styles.imageTone1,
    },
    {
      key: "image3",
      title: currentItem.title3,
      subtitle: currentItem.time3 ? `${currentItem.time3} sec` : "Virtual Try On 001 · 2K",
      image: currentItem.image3,
      altText: "Virtual Try On 001 (Resolution - 2K) Result",
      toneClass: styles.imageTone2,
    },
  ].filter((option) => option.image);

  const activeSourcePreview =
    sourcePreviewOptions.find((option) => option.key === sourcePreviewKey) ||
    sourcePreviewOptions[0] ||
    null;

  const activeResultPreview =
    resultPreviewOptions.find((option) => option.key === resultPreviewKey) ||
    resultPreviewOptions[0] ||
    null;

  const renderOptionButton = (option, isActive, onClick) => (
    <button
      type="button"
      className={`${styles.previewOption} ${isActive ? styles.previewOptionActive : ""}`}
      onClick={onClick}
      aria-pressed={isActive}
      aria-label={`Show ${option.title}`}
    >
      <span className={styles.previewOptionThumbWrap}>
        <img src={option.image} alt={option.altText} className={styles.previewOptionThumb} loading="eager" fetchPriority="high" decoding="sync" />
      </span>
      <span className={styles.previewOptionText}>
        <span className={styles.previewOptionTitle}>{option.title}</span>
        {option.subtitle && <span className={styles.previewOptionSubtitle}>{option.subtitle}</span>}
      </span>
    </button>
  );

  const getTitleToneClass = (title = "") => {
    const normalizedTitle = title.trim().toLowerCase();

    if (normalizedTitle === "nano banana 2 (resolution - 0.5k)") return styles.titleTone0;
    if (normalizedTitle === "nano banana 2 (resolution - 1k)") return styles.titleTone1;
    if (normalizedTitle === "virtual try on 001 (resolution - 2k)") return styles.titleTone2;

    return styles.titleToneDefault;
  };

  const renderComparisonColumn = (title, image, time, altText, imageToneClass) => (
    <div className={styles.columnSlot}>
      <section className={`${styles.comparisonCard} flex-fill d-flex flex-column w-full`}>
        <div className={styles.cardHeader}>
          <div className="flex flex-col text-left">
            <span className={styles.cardEyebrow}>Try-On Output</span>
            <h6 className={`${styles.colTitle} ${getTitleToneClass(title)}`}>
              <span className={styles.titleWithTick}>
                <span className="truncate max-w-[130px]" title={title}>{title}</span>
                {isNanoBanana2(title) && (
                  <span className={styles.tickIcon} aria-hidden="true"> ✓ </span>
                )}
              </span>
            </h6>
          </div>
        </div>

        <div className={`${styles.imageFrame} ${imageToneClass}`}>
          {image ? (
            <img
              src={image}
              alt={altText}
              className={styles.cardImage}
              loading="eager"
              fetchPriority="high"
              decoding="sync"
            />
          ) : (
            <div className={styles.placeholder}>No Image</div>
          )}
        </div>

        <div className={styles.metricsContainer}>
          <div className={styles.metricRow}>
            <span className={styles.metricLabel}>Speed</span>
            <span className={styles.metricValue}>{time ? `${time} sec` : ""}</span>
          </div>
        </div>
      </section>
    </div>
  );

  if (!transformedItems || transformedItems.length === 0) return null;

  return createPortal(
    <div
      className={`${styles.modalRoot} modal fade`}
      id={modalId}
      tabIndex="-1"
      aria-labelledby={`${modalId}Label`}
      aria-hidden="true"
    >
      <div className={`modal-dialog modal-dialog-centered ${styles.modalDialog}`}>
        <div
          className={`modal-content position-relative ${styles.modalContent}`}
        >
          <div className={styles.actionsBar}>
            <button
              type="button"
              className={`${styles.flipButton} ${isFlipped ? styles.flipButtonActive : ""}`}
              aria-label={isFlipped ? "Return to comparison view" : "Flip to preview view"}
              aria-pressed={isFlipped}
              onClick={handleFlip}
            >
              <span className={styles.flipIcon} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`${styles.closeButton}`}
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={handleClose}
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            <div className={styles.headerShell}>
              <div className={styles.headerCopy}>
                <span className={styles.headerEyebrow}>Comparison view</span>
                <h5
                  className={`mb-1 text-uppercase fw-bold ${styles.modalTitle}`}
                >
                  Side-by-Side Comparison
                </h5>
              </div>
              <div className={styles.headerBadge}>
                <span className={styles.headerBadgeValue}>{transformedItems[index]?.sn || index + 1}</span>
                <span className={styles.headerBadgeSeparator}>/</span>
                <span className={styles.headerBadgeTotal}>{totalItems || transformedItems.length}</span>
              </div>
            </div>

            <Carousel
              activeIndex={index}
              onSelect={handleSelect}
              interval={null}
              indicators={false}
              touch={false}
              className={styles.carousel}
              prevIcon={
                <div className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-md hover:bg-slate-50 transition-all text-slate-500 hover:text-primary">
                  <MdChevronLeft size={22} />
                </div>
              }
              nextIcon={
                <div className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-md hover:bg-slate-50 transition-all text-slate-500 hover:text-primary">
                  <MdChevronRight size={22} />
                </div>
              }
            >
              {transformedItems.map((item, idx) => (
                <Carousel.Item key={idx}>
                  {!isFlipped ? (
                    <div className={styles.comparisonGrid}>
                      
                      {/* Card 1: Person */}
                      {item.ModelImage && (
                        <div className={styles.columnSlot}>
                          <section className={`${styles.comparisonCard} flex-fill d-flex flex-column w-full`}>
                            <div className={styles.cardHeader}>
                              <div className="flex flex-col text-left">
                                <span className={styles.cardEyebrow}>Input Source</span>
                                <h6 className={styles.colTitle}>Person {item.personId}</h6>
                              </div>
                            </div>
                            <div className={styles.imageFrame}>
                              <img
                                src={item.ModelImage}
                                alt={`Person ${item.personId}`}
                                className={styles.cardImage}
                                loading="eager"
                                fetchPriority="high"
                                decoding="sync"
                              />
                            </div>
                            <div className={styles.metricsContainer}>
                              <div className={styles.metricRow} aria-hidden="true">
                                <span className={styles.metricLabel}>&nbsp;</span>
                                <span className={styles.metricValue}>&nbsp;</span>
                              </div>
                            </div>
                          </section>
                        </div>
                      )}

                      {/* Card 2: Product */}
                      {item.productImage && (
                        <div className={styles.columnSlot}>
                          <section className={`${styles.comparisonCard} flex-fill d-flex flex-column w-full`}>
                            <div className={styles.cardHeader}>
                              <div className="flex flex-col text-left">
                                <span className={styles.cardEyebrow}>Input Source</span>
                                <h6 className={styles.colTitle}>Product {item.productId}</h6>
                              </div>
                            </div>
                            <div className={styles.imageFrame}>
                              <img
                                src={item.productImage}
                                alt={`Product ${item.productId}`}
                                className={styles.cardImage}
                                loading="eager"
                                fetchPriority="high"
                                decoding="sync"
                              />
                            </div>
                            <div className={styles.metricsContainer}>
                              <div className={styles.metricRow} aria-hidden="true">
                                <span className={styles.metricLabel}>&nbsp;</span>
                                <span className={styles.metricValue}>&nbsp;</span>
                              </div>
                            </div>
                          </section>
                        </div>
                      )}

                      {/* Card 3: Result 1 */}
                      {renderComparisonColumn(
                        item.title1,
                        item.image1,
                        item.time1,
                        "Nano Banana 2 (Resolution - 0.5K) Result",
                        styles.imageTone0
                      )}

                      {/* Card 4: Result 2 */}
                      {renderComparisonColumn(
                        item.title2,
                        item.image2,
                        item.time2,
                        "Nano Banana 2 (Resolution - 1K) Result",
                        styles.imageTone1
                      )}

                      {/* Card 5: Result 3 */}
                      {renderComparisonColumn(
                        item.title3,
                        item.image3,
                        item.time3,
                        "Virtual Try On 001 (Resolution - 2K) Result",
                        styles.imageTone2
                      )}

                    </div>
                  ) : (
                    <div className={`${styles.flipLayout} row g-3 g-lg-4 px-1 px-md-2 px-lg-3 align-items-stretch`}>
                      <div className="col-12 col-lg-6">
                        <section className={styles.previewPanel}>
                          <div className={styles.previewPanelHeader}>
                            <span className={styles.previewPanelEyebrow}>Person and product</span>
                            <h6 className={styles.previewPanelTitle}>Source preview</h6>
                          </div>

                          {activeSourcePreview && (
                            <div className={`${styles.previewHero} ${styles.previewHeroCompact}`}>
                              <div className={`${styles.previewFrame} ${styles.previewHeroFrame}`}>
                                <img
                                  src={activeSourcePreview.image}
                                  alt={activeSourcePreview.altText}
                                  className={`${styles.compareImage} ${styles.previewHeroImage}`}
                                  loading="eager"
                                  fetchPriority="high"
                                  decoding="sync"
                                />
                                <button
                                  type="button"
                                  className={styles.zoomButton}
                                  onClick={() => handleZoomImage(activeSourcePreview.image)}
                                  aria-label="Zoom image"
                                  title="Click to zoom"
                                >
                                  <MdZoomIn size={20} aria-hidden="true" />
                                </button>
                              </div>
                              <div className={styles.previewHeroMeta}>
                                <h6 className={styles.previewTileTitle}>{activeSourcePreview.title}</h6>
                                <span className={styles.previewTileSubtitle}>{activeSourcePreview.subtitle}</span>
                              </div>
                            </div>
                          )}

                          <div className={styles.previewOptionsRow}>
                            {sourcePreviewOptions.map((option) =>
                              renderOptionButton(
                                option,
                                option.key === sourcePreviewKey,
                                () => setSourcePreviewKey(option.key)
                              )
                            )}
                          </div>
                        </section>
                      </div>

                      <div className="col-12 col-lg-6">
                        <section className={styles.previewPanel}>
                          <div className={styles.previewPanelHeader}>
                            <span className={styles.previewPanelEyebrow}>Comparison outputs</span>
                            <h6 className={styles.previewPanelTitle}>
                              Result preview {activeResultPreview && `[${activeResultPreview.title}]`}
                            </h6>
                          </div>

                          {activeResultPreview && (
                            <div className={styles.previewHero}>
                              <div className={`${styles.previewFrame} ${activeResultPreview.toneClass} ${styles.previewHeroFrame}`}>
                                <img
                                  src={activeResultPreview.image}
                                  alt={activeResultPreview.altText}
                                  className={`${styles.compareImage} ${styles.previewHeroImage}`}
                                  loading="eager"
                                  fetchPriority="high"
                                  decoding="sync"
                                />
                                <button
                                  type="button"
                                  className={styles.zoomButton}
                                  onClick={() => handleZoomImage(activeResultPreview.image)}
                                  aria-label="Zoom image"
                                  title="Click to zoom"
                                >
                                  <MdZoomIn size={20} aria-hidden="true" />
                                </button>
                              </div>
                              <div className={styles.previewHeroMeta}>
                                <h6 className={styles.previewTileTitle}>{activeResultPreview.title}</h6>
                                <span className={styles.previewTileSubtitle}>{activeResultPreview.subtitle}</span>
                              </div>
                            </div>
                          )}

                          <div className={`${styles.previewOptionsRow} ${styles.previewOptionsRowWide}`}>
                            {resultPreviewOptions.map((option) =>
                              renderOptionButton(
                                option,
                                option.key === resultPreviewKey,
                                () => setResultPreviewKey(option.key)
                              )
                            )}
                          </div>
                        </section>
                      </div>
                    </div>
                  )}
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CompareZoomModal;
